import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Panel } from "../components/ui";

/** A step of the guide: a number, a heading, and what to do. */
function Step({ n, title, to, children }: { n: number; title: string; to?: string; children: ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-600 text-sm font-semibold text-white">{n}</span>
      <div className="min-w-0 flex-1 pb-1">
        <h3 className="text-sm font-semibold text-ink-900">
          {to ? (
            <Link to={to} className="hover:underline">
              {title}
            </Link>
          ) : (
            title
          )}
        </h3>
        <div className="mt-1 space-y-2 text-sm leading-relaxed text-ink-700">{children}</div>
      </div>
    </li>
  );
}

/**
 * For friends and family trying the app: the ten minutes that get a card set
 * up and a spend scored, and the few things that aren't obvious.
 */
export default function GuidePage() {
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Getting started"
        subtitle="Ten minutes to set up your cards and see what each spend really earns. You're trying an early version — tell us what's wrong or missing with Suggest a change in the menu."
      />
      <div className="space-y-6">
        <Panel title="Set up">
          <ol className="space-y-5">
            <Step n={1} title="Add your cards from the catalogue" to="/cards">
              <p>
                Cards &amp; rules → <strong>Add from catalogue</strong>. Pick your card and its earn rates, caps, exclusions and
                milestones come with it. Add your statement date and due date — caps and bills follow them.
              </p>
              <p>Not in the catalogue? Use <strong>Add manually</strong> and set its base rate.</p>
            </Step>
            <Step n={2} title="Check the card against your bank's terms" to="/cards">
              <p>
                Open the card's rules. The catalogue follows each bank's published terms, but banks change them often — if a rate or
                cap looks off, change it and send a <strong>Suggest a change → A card's rates are off</strong> so everyone gets the
                fix.
              </p>
            </Step>
            <Step n={3} title="Enter your credit limit" to="/cards">
              <p>
                <strong>Edit</strong> the card → Credit limit. If your bank gives one limit across all your cards with it (HDFC does),
                tick <strong>Shared with my other … cards</strong> on each — the Dashboard then shows what's available across them.
              </p>
            </Step>
          </ol>
        </Panel>

        <Panel title="Log your spends">
          <ol className="space-y-5">
            <Step n={4} title="Add a spend" to="/transactions">
              <p>
                Transactions → <strong>Add spend</strong>: amount, merchant, category. The points it earns — and why — show before
                you save.
              </p>
              <p>
                Booked on a portal like HDFC <strong>SmartBuy</strong>, Axis <strong>Travel EDGE</strong> or Kotak
                <strong> Unbox</strong>? Put it in <strong>Booked through</strong>. Your statement shows only "INDIGO" or "MYNTRA",
                and without it the app can't know you earned 5X or 10X.
              </p>
            </Step>
            <Step n={5} title="Or bring them in in bulk">
              <p>
                <Link to="/import" className="font-medium text-accent-700 underline">Import statements</Link> reads a bank statement
                PDF or spreadsheet; <Link to="/slips" className="font-medium text-accent-700 underline">Charge slips</Link> reads a
                photo of the slip; <Link to="/email" className="font-medium text-accent-700 underline">Email alerts</Link> picks up
                your bank's transaction emails.
              </p>
            </Step>
            <Step n={6} title="After changing any rule, recalculate" to="/transactions">
              <p>
                Transactions → <strong>Recalculate points</strong> re-scores what you've already logged against the rules as they
                are now.
              </p>
            </Step>
          </ol>
        </Panel>

        <Panel title="Use it">
          <ol className="space-y-5">
            <Step n={7} title="Which card? before you pay" to="/optimizer">
              <p>
                Enter an amount and category (and the merchant, or "SmartBuy") and every card is ranked by what it would really
                return — caps you've used up, milestones it would unlock, forex on a foreign spend.
              </p>
            </Step>
            <Step n={8} title="Watch your caps" to="/">
              <p>
                The Dashboard shows each accelerated cap: what you've earned this month, what's left, and how much more you can spend
                in each category before it's used up. <strong>Plan a mix</strong> splits what's left across categories. Some offers
                have their own smaller cap inside — SmartBuy vouchers get 3,000 of the 15,000 — shown under the same cap.
              </p>
            </Step>
            <Step n={9} title="Milestones and points" to="/milestones">
              <p>
                <Link to="/milestones" className="font-medium text-accent-700 underline">Milestones</Link> tracks spend toward fee
                waivers and bonuses. <Link to="/points" className="font-medium text-accent-700 underline">Points &amp; redemptions</Link>{" "}
                shows balances, when points expire, and bonus points your bank credits later — so you can check they arrived.
              </p>
            </Step>
            <Step n={10} title="Share a card with friends" to="/shared">
              <p>
                Set up a card well? <strong>Share</strong> its template or a rule with people you choose. Only the card's rules go —
                never your spends or balances.
              </p>
            </Step>
          </ol>
        </Panel>

        <Panel title="Good to know">
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ink-700">
            <li>
              <strong>Your data is yours.</strong> Nobody else can see your cards, spends or balances. Never type a full card number
              anywhere — the last four digits are all the app needs.
            </li>
            <li>
              <strong>Stay up to date.</strong> The bottom of the menu shows the version you're on. If it says a newer one is live,
              tap <strong>Update now</strong>.
            </li>
            <li>
              <strong>On a phone,</strong> add the site to your home screen (Share → Add to Home Screen) and it opens like an app.
            </li>
            <li>
              <strong>Found something wrong?</strong> <strong>Suggest a change</strong>, at the bottom of the menu, works from any
              page and tells us which page you were on. Replies show under{" "}
              <Link to="/feedback" className="font-medium text-accent-700 underline">Feedback</Link>.
            </li>
          </ul>
        </Panel>
      </div>
    </div>
  );
}
